import { useEffect } from 'react';
import { useGet, usePost } from '../hooks';
import { useMovieStore } from '../stores';
import { Editor } from '../components/Editor/Editor';
import type { MovieResponse, MovieSummary } from '../types';
import './EditorPage.css';
import { Logo } from '../components/Logo';

const normalizeDimension = (value: number): number => (value > 0 ? value : 500);

export function EditorPage() {
  // Fetch all three movie lists from separate endpoints
  const { data: allMoviesData, loading: allLoading, error: allError } = useGet<MovieResponse[]>('/api/movies/getUserAllMovies');
  const { data: recentMoviesData, execute: refreshRecentMovies } = useGet<MovieResponse[]>('/api/movies/getUserRecentMovies');
  const { data: sampleMoviesData } = useGet<MovieResponse[]>('/api/movies/getSampleMovies');

  const {
    userAllMovies,
    userRecentMovies,
    setUserAllMovies,
    setUserRecentMovies,
    setSampleMovies,
    selectedMovieId,
  } = useMovieStore();
  const selectedMovieUrl = selectedMovieId ? `/api/movies/${selectedMovieId}` : null;
  const { data: selectedMovie } = useGet<MovieResponse>(selectedMovieUrl, { enabled: !!selectedMovieId });
  const trackRecentUrl = selectedMovieId ? `/api/movies/recent/${selectedMovieId}` : null;
  const { execute: trackRecentMovie } = usePost<unknown, void>(trackRecentUrl);

  // Populate userAllMovies
  useEffect(() => {
    if (!allMoviesData) return;
    const allMovies: MovieSummary[] = allMoviesData.map((movie) => ({
      id: movie.id,
      name: movie.name,
      description: movie.description ?? '',
      width: normalizeDimension(movie.width),
      height: normalizeDimension(movie.height),
      fps: movie.fps,
      sceneCount: movie.sceneCount ?? 0,
      sceneObjectCount: movie.sceneObjectCount ?? 0,
      layerCount: movie.layerCount ?? 0,
      isSample: false,
    }));
    setUserAllMovies(allMovies);
  }, [allMoviesData, setUserAllMovies]);

  // Populate userRecentMovies
  useEffect(() => {
    if (!recentMoviesData) return;
    const recentMovies: MovieSummary[] = recentMoviesData.map((movie) => ({
      id: movie.id,
      movieId: movie.id,
      name: movie.name,
      description: movie.description ?? '',
      width: normalizeDimension(movie.width),
      height: normalizeDimension(movie.height),
      fps: movie.fps,
      sceneCount: movie.sceneCount ?? 0,
      sceneObjectCount: movie.sceneObjectCount ?? 0,
      layerCount: movie.layerCount ?? 0,
      isSample: false,
    }));
    setUserRecentMovies(recentMovies);
  }, [recentMoviesData, setUserRecentMovies]);

  // Populate sampleMovies
  useEffect(() => {
    if (!sampleMoviesData) return;
    const sampleMovies: MovieSummary[] = sampleMoviesData.map((movie) => ({
      id: movie.id,
      name: movie.name,
      description: movie.description ?? '',
      width: normalizeDimension(movie.width),
      height: normalizeDimension(movie.height),
      fps: movie.fps,
      sceneCount: movie.sceneCount ?? 0,
      sceneObjectCount: movie.sceneObjectCount ?? 0,
      layerCount: movie.layerCount ?? 0,
      isSample: true,
    }));
    setSampleMovies(sampleMovies);
  }, [sampleMoviesData, setSampleMovies]);

  // Ensure selected movie is reflected in "My Projects" immediately.
  // This keeps UI in sync after opening/copying from sample without requiring reload.
  // Guard with selectedMovieId so unload/delete does not re-add a stale selectedMovie
  // while useGet still holds the previous response for one render.
  useEffect(() => {
    if (!selectedMovieId || !selectedMovie || selectedMovie.id !== selectedMovieId) return;

    const selectedSummary: MovieSummary = {
      id: selectedMovie.id,
      name: selectedMovie.name,
      description: selectedMovie.description ?? '',
      width: normalizeDimension(selectedMovie.width),
      height: normalizeDimension(selectedMovie.height),
      fps: selectedMovie.fps,
      sceneCount: selectedMovie.sceneCount ?? 0,
      sceneObjectCount: selectedMovie.sceneObjectCount ?? 0,
      layerCount: selectedMovie.layerCount ?? 0,
      isSample: false,
    };

    const isSameSummary = (movie: MovieSummary) =>
      (movie.name ?? '') === selectedSummary.name &&
      (movie.description ?? '') === selectedSummary.description &&
      movie.width === selectedSummary.width &&
      movie.height === selectedSummary.height &&
      movie.fps === selectedSummary.fps &&
      (movie.sceneCount ?? 0) === (selectedSummary.sceneCount ?? 0) &&
      (movie.sceneObjectCount ?? 0) === (selectedSummary.sceneObjectCount ?? 0) &&
      (movie.layerCount ?? 0) === (selectedSummary.layerCount ?? 0) &&
      movie.isSample === selectedSummary.isSample;

    const existingMovie = userAllMovies.find((movie) => movie.id === selectedMovie.id);
    const existingRecent = userRecentMovies.find(
      (movie) => (movie.movieId ?? movie.id) === selectedMovie.id
    );

    const allNeedsUpdate = !existingMovie || !isSameSummary(existingMovie);
    const recentNeedsUpdate = !!existingRecent && !isSameSummary(existingRecent);

    if (!allNeedsUpdate && !recentNeedsUpdate) {
      return;
    }

    if (allNeedsUpdate) {
      if (existingMovie) {
        setUserAllMovies(
          userAllMovies.map((movie) =>
            movie.id === selectedMovie.id ? { ...movie, ...selectedSummary } : movie
          )
        );
      } else {
        setUserAllMovies([selectedSummary, ...userAllMovies]);
      }
    }

    if (recentNeedsUpdate && existingRecent) {
      setUserRecentMovies(
        userRecentMovies.map((movie) =>
          (movie.movieId ?? movie.id) === selectedMovie.id
            ? { ...movie, ...selectedSummary, movieId: selectedMovie.id }
            : movie
        )
      );
    }
  }, [
    selectedMovie,
    selectedMovieId,
    userAllMovies,
    userRecentMovies,
    setUserAllMovies,
    setUserRecentMovies,
  ]);

  // Track selected movie as recent and refresh recent list.
  useEffect(() => {
    if (!selectedMovieId) return;

    let isActive = true;
    const trackAndRefresh = async () => {
      await trackRecentMovie();
      if (!isActive) return;
      await refreshRecentMovies();
    };

    void trackAndRefresh();
    return () => {
      isActive = false;
    };
  }, [selectedMovieId, trackRecentMovie, refreshRecentMovies]);

  // Loading state - only block on allMovies (required), others load in background
  if (allLoading) {
    return (
      <div className="editor-page-loading">
        <div className="loading-spinner" />
        <Logo />
        <span>Loading movies...</span>
      </div>
    );
  }

  // Error state
  if (allError) {
    return (
      <div className="editor-page-error">
        <span>Failed to load movies: {allError}</span>
        <button onClick={() => window.location.reload()}>Retry</button>
      </div>
    );
  }

  // Pass selected movie to Editor
  return <Editor currentMovie={selectedMovie} />;
}
